import { ClientMessage } from '@common/api';

export interface WebSocketController {
  sendMessage: (message: ClientMessage) => void;
  closeWebSocket: () => void;
}

export interface EventHandlers {
  onOpen: (event: Event) => void;
  onMessage: (event: MessageEvent) => void;
  onClose: (event: CloseEvent) => void;
  onError: (event: Event) => void;
}

/**
 * Custom error for authentication and authorization failures.
 */
export class AuthError extends Error {
  constructor(
    public readonly status: number,
    message?: string,
  ) {
    super(message || `Auth error: ${status}`);
    this.name = 'AuthError';
  }
}

/**
 * Establishes a session by calling the /session endpoint.
 * This sets the session cookie which will be sent with the WebSocket upgrade.
 */
async function establishSession(sessionUrl: URL): Promise<void> {
  try {
    const response = await fetch(sessionUrl.href, {
      method: 'GET',
      credentials: 'include',
    });

    if (response.status === 401 || response.status === 403) {
      throw new AuthError(response.status);
    }

    if (!response.ok) {
      console.error('Failed to establish session:', response.status);
      return;
    }

    const data = await response.json();
    console.log('Session established:', data);
  } catch (error) {
    if (error instanceof AuthError) {
      throw error;
    }
    console.error('Error establishing session:', error);
  }
}

export async function connectWebSocket(
  sessionUrl: URL,
  websocketUrl: URL,
  eventHandlers: EventHandlers,
): Promise<WebSocketController> {
  let socket: WebSocket;
  let reconnectAttempts = 0;
  let manuallyClosed = false;
  const useExponentialBackoff = true;

  const init = async () => {
    try {
      await establishSession(sessionUrl);
    } catch (error) {
      if (error instanceof AuthError) {
        if (error.status === 401 || error.status === 403) {
          // Stop reconnection if forbidden or unauthorized
          manuallyClosed = true;
          throw error;
        }
      }
      // For other errors, we might still want to try connecting later
      console.error('Initial session establishment failed, will retry if possible:', error);
    }

    socket = new WebSocket(websocketUrl);
    (<any>window).theSocket = socket; // eslint-disable-line @typescript-eslint/no-explicit-any

    socket.onopen = (event) => {
      console.log('WebSocket connected successfully.');
      reconnectAttempts = 0;
      eventHandlers.onOpen(event);
    };

    socket.onclose = (event) => {
      eventHandlers.onClose(event);

      if (manuallyClosed) {
        return;
      }

      const baseDelay = 1000;
      const maxDelay = 30000;

      let delay = baseDelay;
      if (useExponentialBackoff) {
        delay = Math.min(maxDelay, baseDelay * Math.pow(2, reconnectAttempts));
      }

      console.log(`WebSocket disconnected. Retrying in ${delay / 1000}s...`);

      setTimeout(() => {
        reconnectAttempts++;
        init().catch((err) => {
          console.error('Reconnection attempt failed:', err);
        });
      }, delay);
    };

    socket.onmessage = eventHandlers.onMessage;
    socket.onerror = eventHandlers.onError;
  };

  const sendMessage = (message: ClientMessage): void => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      const fullMessage = { ...message } as ClientMessage;
      socket.send(JSON.stringify(fullMessage));
    } else {
      console.error('WebSocket is not open. Cannot send message.');
    }
  };

  const closeWebSocket = (): void => {
    manuallyClosed = true;
    if (socket) {
      socket.close();
    }
  };

  await init();

  return { sendMessage, closeWebSocket };
}
